/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { SourceFile } from "@sandbox/code-editor";
import { AttributeSerializer } from "services/share-service/serializers/AttributeSerializer";
import { OptionalFileSet } from "types/codeshareTypes";
import { SandboxApi } from "./types";

export const codeShareMetadataListTransform = (response: SandboxApi.CodeShareMetadataResponse[]) => {
  return response.map((share) => codeShareMetadataTransform(share));
};

export const codeShareMetadataTransform = (response: SandboxApi.CodeShareMetadataResponse) => {
  return {
    ...response,
    attributes: AttributeSerializer.deserialize(response.attributes),
    itwinVersion: AttributeSerializer.getITwinVersion(response.attributes)
  };
};

export const sourceFilesTransform = (files: SandboxApi.SandboxFiles, readOnly?: true) => {
  const transformed: SourceFile[] = [];

  Object.keys(files).forEach((filePath) => {
    const filePathAbsolute = filePath.startsWith("/") ? filePath : `/${filePath}`;

    if (files[filePath] && files[filePath].content)
      transformed.push({
        filePath: filePathAbsolute,
        type: "file",
        content: files[filePath].content,
        entry: files[filePath].entry,
        readOnly
      })
    else
      transformed.push({
        filePath: filePathAbsolute,
        type: "folder",
        readOnly
      })
  });

  return transformed;
}

export const sourceFilesTransformBack = (files: SourceFile[]) => {
  const transformed: SandboxApi.SandboxFiles = {};

  for (let i = 0; i < files.length; i++) {
    if (files[i].type === "folder")
      continue;

    transformed[files[i].filePathRenamed ?? files[i].filePath] = {
      content: files[i].content,
      entry: files[i].entry
    }
  }

  return transformed;
}

export const optionalFilesTransform = (files: SandboxApi.OptionalFileSet[]): OptionalFileSet[] => {
  const transformed: OptionalFileSet[] = [];
  files.forEach((item) => {
    transformed.push({
      ...item,
      files: sourceFilesTransform(item.files, true)
    })
  })
  return transformed;
}

export const getOptionalFiles = (optionalFileSet: OptionalFileSet[]) => {
  return optionalFileSet.flatMap((item) => item.files);
}